/**
 * Extensões da classe nativa `String` do TypeScript.
 *
 * Este arquivo augmenta globalmente a interface `String` com métodos utilitários
 * de uso frequente para manipulação, validação e conversão de strings.
 *
 * **Importante:** este arquivo deve ser importado uma única vez na aplicação
 * (ex.: `main.ts`) para que as extensões de protótipo sejam registradas.
 */
export {}

declare global {
    interface String {
        /**
         * Remove acentos, diacríticos e cedilha de todos os caracteres da string,
         * retornando uma versão normalizada em ASCII.
         *
         * Utiliza a normalização Unicode NFD para decompor os caracteres acentuados
         * em seus componentes base + diacrítico e, em seguida, remove os diacríticos
         * com uma expressão regular. A cedilha (ç/Ç) é tratada separadamente.
         *
         * @returns Nova string sem acentos, diacríticos ou cedilha.
         *
         * @example
         * 'Olá, João!'.removeAccentsAndCedilla()       // 'Ola, Joao!'
         * 'Ação Coração'.removeAccentsAndCedilla()      // 'Acao Coracao'
         * 'Çaça de Leão'.removeAccentsAndCedilla()      // 'Caca de Leao'
         */
        removeAccentsAndCedilla(): string;

        /**
         * Retorna as iniciais (primeira letra maiúscula) de cada palavra da string.
         *
         * Divide a string por espaços, extrai o primeiro caractere de cada palavra,
         * converte para maiúsculo e concatena os resultados. O parâmetro `n` limita
         * quantas iniciais serão retornadas; caso omitido, retorna todas.
         *
         * @param n Número máximo de iniciais a serem retornadas.
         *          Se `undefined`, retorna as iniciais de todas as palavras.
         * @returns String com as iniciais concatenadas.
         *
         * @example
         * 'João da Silva'.getFirstLetterOfEachWord(undefined) // 'JDS'
         * 'Maria Oliveira Santos'.getFirstLetterOfEachWord(2) // 'MO'
         * 'ana'.getFirstLetterOfEachWord(1)                   // 'A'
         */
        getFirstLetterOfEachWord(n: number | undefined): string;

        /**
         * Verifica se a string está vazia ou contém apenas espaços em branco.
         *
         * Realiza `trim()` antes da verificação, portanto strings compostas
         * exclusivamente por espaços, tabs ou quebras de linha são consideradas vazias.
         *
         * @returns `true` se a string estiver vazia ou for apenas whitespace,
         *          `false` caso contrário.
         *
         * @example
         * ''.isEmpty()       // true
         * '   '.isEmpty()    // true
         * 'texto'.isEmpty()  // false
         */
        isEmpty(): boolean;

        /**
         * Verifica se a string possui ao menos um caractere não-branco.
         *
         * É o oposto semântico de `isEmpty()`. Realiza `trim()` antes da verificação.
         *
         * @returns `true` se a string possuir conteúdo além de whitespace,
         *          `false` caso contrário.
         *
         * @example
         * 'texto'.isNotEmpty()  // true
         * '  a  '.isNotEmpty()  // true
         * '   '.isNotEmpty()    // false
         */
        isNotEmpty(): boolean;

        /**
         * Formata uma string numérica como CNPJ no padrão `XX.XXX.XXX/XXXX-XX`.
         *
         * Remove todos os caracteres não numéricos antes de aplicar a máscara
         * e limita a entrada a 14 dígitos. Útil para formatar CNPJ vindo de
         * APIs ou digitado pelo usuário sem formatação.
         *
         * @returns String formatada no padrão de CNPJ.
         *
         * @example
         * '11222333000181'.formatCNPJ()   // '11.222.333/0001-81'
         * '11.222.333/0001-81'.formatCNPJ() // '11.222.333/0001-81' (idempotente)
         * '112223330001'.formatCNPJ()     // '11.222.333/0001' (incompleto)
         */
        formatCNPJ(): string;

        /**
         * Verifica se a string é um endereço de e-mail válido.
         *
         * A validação utiliza uma expressão regular que verifica o formato básico
         * `local@domínio.extensão`, onde a extensão deve ter ao menos 2 caracteres.
         * Não realiza verificação de existência do endereço (DNS/MX lookup).
         *
         * @returns `true` se a string corresponder ao padrão de e-mail, `false` caso contrário.
         *
         * @example
         * 'usuario@exemplo.com'.isEmail()    // true
         * 'usuario@sub.exemplo.com'.isEmail() // true
         * 'invalido@'.isEmail()              // false
         * 'sem-arroba.com'.isEmail()         // false
         */
        isEmail(): boolean;

        /**
         * Converte a string para um número inteiro.
         *
         * Normaliza o formato brasileiro antes da conversão: remove pontos de milhar
         * (`.`) e substitui a vírgula decimal (`,`) por ponto (`.`). Utiliza
         * `parseInt` com base 10. Retorna `0` se a conversão falhar (NaN).
         *
         * @returns Número inteiro representado pela string, ou `0` se inválido.
         *
         * @example
         * '42'.toInt()        // 42
         * '1.500'.toInt()     // 1500
         * '3,7'.toInt()       // 3
         * 'abc'.toInt()       // 0
         */
        toInt(): number;

        /**
         * Converte a string para um número de ponto flutuante.
         *
         * Normaliza o formato brasileiro antes da conversão: remove pontos de milhar
         * (`.`) e substitui a vírgula decimal (`,`) por ponto (`.`). Utiliza
         * `parseFloat` nativamente.
         *
         * @returns Número float representado pela string, ou `NaN` se inválido.
         *
         * @example
         * '3,14'.toFloat()     // 3.14
         * '1.234,56'.toFloat() // 1234.56
         * '42'.toFloat()       // 42
         * 'abc'.toFloat()      // NaN
         */
        toFloat(): number;

        /**
         * Converte a string para um valor booleano.
         *
         * Realiza a conversão de forma case-insensitive, ignorando espaços nas
         * extremidades. São considerados `true` os valores: `"true"`, `"1"` e `"yes"`.
         * Qualquer outro valor retorna `false`.
         *
         * @returns `true` para os valores `"true"`, `"1"` ou `"yes"` (case-insensitive),
         *          `false` para qualquer outro valor.
         *
         * @example
         * 'true'.toBoolean()  // true
         * 'TRUE'.toBoolean()  // true
         * '1'.toBoolean()     // true
         * 'yes'.toBoolean()   // true
         * 'false'.toBoolean() // false
         * '0'.toBoolean()     // false
         */
        toBoolean(): boolean;

        /**
         * Trunca a string até o limite de caracteres especificado, adicionando um
         * sufixo ao final caso o texto seja cortado.
         *
         * Caso o texto seja menor ou igual ao limite, retorna o texto original sem
         * alterações. O sufixo padrão é `'...'` mas pode ser personalizado.
         *
         * @param limit Número máximo de caracteres antes do sufixo.
         * @param trail Sufixo a ser adicionado ao texto truncado. Padrão: `'...'`.
         * @returns String truncada com o sufixo, ou a string original se dentro do limite.
         *
         * @example
         * 'Olá, mundo!'.truncate(5)          // 'Olá, ...'
         * 'Olá, mundo!'.truncate(5, ' [...]') // 'Olá,  [...]'
         * 'Ok'.truncate(10)                  // 'Ok'
         */
        truncate(limit: number, trail: string): string;

        /**
         * Converte uma string Base64 em um objeto `File`.
         *
         * Decodifica o conteúdo Base64 usando `atob`, constrói um `Uint8Array`
         * com os bytes resultantes e cria um `File` com o tipo MIME inferido
         * a partir da extensão do nome do arquivo.
         *
         * @param filename Nome do arquivo resultante, incluindo a extensão
         *                 (ex.: `"imagem.png"`). A extensão é usada para
         *                 definir o tipo MIME no formato `image/<extensão>`.
         * @returns Objeto `File` construído a partir do conteúdo Base64.
         *
         * @example
         * const base64 = '/9j/4AAQSkZJRgAB...';
         * const file = base64.toFile('foto.jpg');
         * // File { name: 'foto.jpg', type: 'image/jpg' }
         */
        toFile(filename: string): File;

        /**
         * Verifica se a string é um conteúdo HTML puro, ou seja, formado
         * **exclusivamente por tags**, sem texto solto no nível raiz do `<body>`.
         *
         * Utiliza `DOMParser` para interpretar a string como HTML e verifica
         * se o `<body>` resultante contém ao menos um elemento filho e nenhum
         * nó de texto direto com conteúdo não-vazio.
         *
         * O conteúdo textual **dentro** das próprias tags é permitido e não
         * influencia o resultado.
         *
         * @returns `true` se a string for composta apenas por tags HTML,
         *          `false` caso contrário.
         *
         * @example
         * '<p>Olá mundo</p>'.isHTML()            // true  — texto dentro de tag
         * '<br><hr>'.isHTML()                    // true  — tags void
         * '<div><span>texto</span></div>'.isHTML() // true  — tags aninhadas
         * 'Texto solto <b>negrito</b>'.isHTML()  // false — texto solto no nível raiz
         * 'Apenas texto'.isHTML()                // false — nenhuma tag
         * ''.isHTML()                            // false — string vazia
         */
        isHTML(): boolean;

        /**
         * Verifica se a string é um JSON sintaticamente válido.
         *
         * Aplica uma verificação rápida por expressão regular antes de tentar
         * o `JSON.parse`, descartando com custo mínimo strings que claramente
         * não são JSON. Aceita todos os tipos JSON válidos: objetos `{}`,
         * arrays `[]`, strings `""`, números, `true`, `false` e `null`.
         *
         * @returns `true` se a string puder ser interpretada como JSON válido,
         *          `false` caso contrário.
         *
         * @example
         * '{"nome":"João"}'.isJSON()   // true
         * '[1, 2, 3]'.isJSON()         // true
         * '"texto"'.isJSON()           // true
         * 'true'.isJSON()              // true
         * '42'.isJSON()                // true
         * 'Apenas texto'.isJSON()      // false
         * '{ chave: valor }'.isJSON()  // false
         * ''.isJSON()                  // false
         */
        isJSON(): boolean;

        /**
         * Converte a string para um objeto JavaScript caso seja um JSON válido.
         *
         * Internamente chama `isJSON()` antes de tentar o parse, evitando
         * exceções desnecessárias. Suporta o parâmetro genérico `<T>` para
         * tipagem do resultado em tempo de compilação.
         *
         * @template T Tipo esperado do objeto resultante. Padrão: `unknown`.
         * @returns O valor JavaScript resultante do parse com o tipo `T`,
         *          ou `null` se a string não for um JSON válido.
         *
         * @example
         * '{"nome":"João"}'.toJSON()             // { nome: 'João' }
         * '[1, 2, 3]'.toJSON<number[]>()         // [1, 2, 3]
         * '"texto"'.toJSON<string>()             // 'texto'
         * 'Apenas texto'.toJSON()                // null
         *
         * @example
         * // Tipagem com interface
         * interface Pessoa { nome: string; idade: number; }
         * const p = '{"nome":"Ana","idade":30}'.toJSON<Pessoa>();
         * // p.nome → 'Ana', p.idade → 30
         */
        toJSON<T = unknown>(): T | null;

        /** Converte a string para Title Case (primeira letra de cada palavra em maiúscula). */
        toTitleCase(): string;

        /**
         * Formata uma string de CPF no padrão `000.000.000-00` (parcial enquanto
         * incompleta).
         */
        formatCPF(): string;
    }
}

String.prototype.truncate = function (
    this: string,
    limit: number,
    trail: string = '...',
): string {
    // Se o valor de entrada não for uma string ou estiver vazio, retorna uma string vazia.
    if (!this) {
        return this;
    }

    // Se o texto for menor ou igual ao limite, retorna o texto original sem alterações.
    if (this.length <= limit) {
        return this;
    }

    // Trunca o texto para o limite especificado e adiciona o sufixo.
    return this.substring(0, limit) + trail;
};

String.prototype.isEmail = function (this: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    return emailRegex.test(this);
};

String.prototype.toBoolean = function (this: string): boolean {
    const normalized = this.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

String.prototype.toInt = function (this: string): number {
    const normalized = this.replace(/\./g, '').replace(/,/g, '.');
    const parsed = parseInt(normalized, 10);
    return isNaN(parsed) ? 0 : parsed;
};

String.prototype.toFloat = function (this: string): number {
    const normalized = this.replace(/\./g, '').replace(/,/g, '.');
    return parseFloat(normalized);
};

String.prototype.isNotEmpty = function (this: string): boolean {
    return this.trim().length > 0;
};

String.prototype.isEmpty = function (this: string): boolean {
    return this.trim().length === 0;
};

String.prototype.removeAccentsAndCedilla = function (this: string): string {
    return this.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/ç/g, 'c')
        .replace(/Ç/g, 'C');
};

String.prototype.getFirstLetterOfEachWord = function (
    this: string,
    n: number | undefined,
): string {
    return this.split(' ')
        .map((namePart) => namePart.charAt(0).toUpperCase())
        .slice(0, n ?? this.split(' ').length)
        .join('');
};

String.prototype.formatCNPJ = function (this: string): string {
    const v = this.replace(/\D/g, '').slice(0, 14);
    return v
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
};

String.prototype.toTitleCase = function (this: string): string {
    return this.toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

String.prototype.formatCPF = function (this: string): string {
    const v = this.replace(/\D/g, '').slice(0, 11);
    if (v.length <= 3) return v;
    else if (v.length <= 6) return `${v.slice(0, 3)}.${v.slice(3)}`;
    else if (v.length <= 9)
        return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
    else
        return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
};

String.prototype.toFile = function (this: string, filename: string): File {
    const bstr = atob(this); // decodifica Base64
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, {
        type: `image/${filename.split('.').pop()}`,
    });
};

String.prototype.isHTML = function (this: string): boolean {
    const trimmed = this.trim();

    if (!trimmed) return false;

    // Usa o DOMParser para interpretar a string como HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(trimmed, 'text/html');
    const body = doc.body;

    // Precisa ter ao menos um elemento filho (tag)
    if (body.children.length === 0) return false;

    // Nenhum nó de texto direto no body pode ter conteúdo (texto solto no nível raiz)
    for (const node of Array.from(body.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
            return false;
        }
    }

    return true;
};

String.prototype.isJSON = function (this: string): boolean {
    const trimmed = this.trim();

    if (!trimmed) return false;

    // JSON válido deve começar com {, [, ", número, true, false ou null
    if (!/^[\[{"\-\d]|^(true|false|null)$/.test(trimmed)) return false;

    try {
        JSON.parse(trimmed);
        return true;
    } catch {
        return false;
    }
};

String.prototype.toJSON = function <T = unknown>(this: string): T | null {
    if (!this.isJSON()) return null;

    try {
        return JSON.parse(this.trim()) as T;
    } catch {
        return null;
    }
};

